import {useTemplate} from 'Event/TemplateProvider'
import {useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useConfig} from 'organization/Event/Configurable'
import Config from 'organization/Event/Configurable/Config'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import React from 'react'
import TypographySection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/TypographySection'
import BackgroundSection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/BackgroundSection'
import SizeSection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/SizeSection'
import BorderSection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/BorderSection'
import ContentInput from 'Event/Dashboard/components/NavButton/NavButtonConfig/ContentInput'
import {replaceAtPath} from 'lib/JsonUpdateProvider'
import {useAddTranslation} from 'Event/LanguageProvider'
import {useAttendeeVariables} from 'Event'
import ButtonStyleClipboardActions, {
  ButtonStyleClipboard,
} from 'lib/ui/Button/ButtonStyleClipboardActions'

export default function SubmitButtonConfig() {
  const {
    form: {register, control, watch, setValue},
  } = useConfig()
  const saveTemplate = useSaveTemplate()
  const {
    setPasswordForm: {button},
  } = useTemplate()
  const addTranslation = useAddTranslation()
  const v = useAttendeeVariables()

  const handleSave = (data: any) => {
    const label = replaceAtPath(data, `text`, `{{set_password_form_button}}`)

    saveTemplate({
      setPasswordForm: {
        button: data,
      },
      localization: addTranslation({
        set_password_form_button: label ?? '',
      }),
    })
  }

  const pasteButtonStyle = (style: ButtonStyleClipboard) => {
    const set = (name: string, value: unknown) => {
      if (value === undefined) {
        return
      }

      setValue(name, value, {shouldDirty: true})
    }

    set('disableHover', style.disableHover)

    // The hover color inputs only mount on the next render when the pasted
    // disableHover reveals them, so the rest is set a tick later.
    setTimeout(() => {
      set('font', style.font)
      set('fontSize', style.fontSize)
      set('textColor', style.textColor)
      set('hoverTextColor', style.hoverTextColor)
      set('backgroundColor', style.backgroundColor)
      set('hoverBackgroundColor', style.hoverBackgroundColor)
      set('borderColor', style.borderColor)
      set('hoverBorderColor', style.hoverBorderColor)
      set('borderWidth', style.borderWidth)
      set('borderRadius', style.borderRadius)
      set('height', style.height)
      set('width', style.widthPercent)
    }, 0)
  }

  return (
    <Config
      title="Submit Button"
      onSave={handleSave}
      preview={{
        setPasswordForm: {
          button: watch(),
        },
      }}
    >
      <SettingsPanel>
        <ContentInput
          button={{
            ...button,
            text: v(button.text),
          }}
          control={control}
          register={register}
          watch={watch}
        />
      </SettingsPanel>
      <StylingPanel>
        <ButtonStyleClipboardActions
          values={{
            font: watch('font', button.font),
            fontSize: watch('fontSize', button.fontSize),
            textColor: watch('textColor', button.textColor),
            hoverTextColor: watch('hoverTextColor', button.hoverTextColor),
            backgroundColor: watch('backgroundColor', button.backgroundColor),
            hoverBackgroundColor: watch(
              'hoverBackgroundColor',
              button.hoverBackgroundColor,
            ),
            disableHover: watch('disableHover', button.disableHover),
            borderColor: watch('borderColor', button.borderColor),
            hoverBorderColor: watch(
              'hoverBorderColor',
              button.hoverBorderColor,
            ),
            borderWidth: watch('borderWidth', button.borderWidth),
            borderRadius: watch('borderRadius', button.borderRadius),
            height: watch('height', button.height),
            widthPercent: watch('width', button.width),
          }}
          onPaste={pasteButtonStyle}
        />
        <TypographySection
          button={button}
          control={control}
          storageKey="step1.submitButton.config.styling.typography"
        />
        <BackgroundSection
          button={button}
          control={control}
          watch={watch}
          storageKey="step1.submitButton.config.styling.background"
        />
        <SizeSection
          button={button}
          control={control}
          disableNewLine
          sizeUnit="percent"
          storageKey="step1.submitButton.config.styling.size"
        />
        <BorderSection
          button={button}
          control={control}
          storageKey="step1.submitButton.config.styling.border"
        />
      </StylingPanel>
    </Config>
  )
}
